// @ts-nocheck
import React, {useEffect, useRef, useState} from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Waypoint from "./Waypoint";
import {useLocation, useNavigate} from "react-router-dom";
import Typography from "@mui/material/Typography";
import startSvg from './Arrivo.svg';
import endSvg from './Partenza.svg';
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from "@mui/material/Box";
import {Button, CircularProgress, Collapse} from "@mui/material";
import Paper from "@mui/material/Paper";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Leg from "./Leg";
import {optimizeRoute} from "./KmlImporter";
import WaypointType from "./WaypointType";
import Sidebar from "./Sidebar";
import {Simulate} from "react-dom/test-utils";
import Guid from "./Guid";
import type {DropResult} from "@hello-pangea/dnd";

type EditorProps = {
    updateLeg: (dayId: string, leg: Leg) => Promise<void>,
};

type EditorState = {
    dayId: string,
    leg: Leg
}

const MapBoxEditor = ({ updateLeg }: EditorProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const mapRef = useRef();
    const mapContainerRef = useRef();
    const [legTitle, setLegTitle] = useState("");
    const [tolerance, setTolerance] = useState<number>(100);
    const [appliedTolerance, setAppliedTolerance] = useState<number>(100);
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isKml, setIsKml] = useState(false);
    const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
    const [isRouteBuilderMode, setIsRouteBuilderMode] = useState(false);
    const [draggingMarkerRef, setDraggingMarkerRef] = useState(null);
    const markersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());
    const accessToken = 'pk.eyJ1IjoicmFkdW5pYXBwIiwiYSI6ImNscmo4c3VuajAwemMya2swZGYxOXJ1NjMifQ.2kCbnBGKIynyls_YE13Mmw';

    useEffect(() => {
        if (!location.state) {
            navigate("/");
            return;
        }

        mapboxgl.accessToken = accessToken;
        const locationState = location.state;
        setLegTitle(locationState.leg.title);
        
        let center = [11.134926939136761, 46.625904867494576];
        if (locationState.leg.route && locationState.leg.route.length > 0) {
            setWaypoints(locationState.leg.route);
            setTolerance(locationState.leg.tolerance || 100);
            setAppliedTolerance(locationState.leg.tolerance || 100);
            center = [locationState.leg.route[0].coordinates.lng, locationState.leg.route[0].coordinates.lat];
        }
        
        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            center: center,
            zoom: locationState.leg.route?.length > 0 ? 12 : 5
        });

        mapRef.current.on("load", () => {
            console.log("map onLoad:" + locationState.leg.route.length);
            if (locationState.leg.route && locationState.leg.route.length > 0) {
                const bounds = calculateBounds(locationState.leg.route);
                mapRef.current.fitBounds([
                    bounds.southwest,
                    bounds.northeast
                ], {animate: false, padding: 100});

                // Add kml route
                if (locationState.leg.isKml && locationState.leg.kmlCoordinates && locationState.leg.kmlCoordinates.length > 1) {
                    console.log("mapboxEditor -> kml opened")
                    setIsKml(true);
                    mapRef.current.addSource("route", {
                        type: "geojson",
                        data: waypointsToGeoJsonLineString(locationState.leg.kmlCoordinates),
                    });

                    mapRef.current.addLayer({
                        id: "kml-route-line",
                        type: "line",
                        source: "route",
                        paint: {
                            "line-color": "#1976d2",
                            "line-width": 6,
                        },
                    });

                    addSvgMarker(locationState.leg.kmlCoordinates[0].lng, locationState.leg.kmlCoordinates[0].lat, `url(${startSvg})`);
                    addSvgMarker(locationState.leg.kmlCoordinates[locationState.leg.kmlCoordinates.length - 1].lng, locationState.leg.kmlCoordinates[locationState.leg.kmlCoordinates.length - 1].lat, `url(${endSvg})`);
                    
                    if (locationState.leg.tolerance) {
                        handleApply(locationState.leg.tolerance);
                    }
                } else {
                    updateMapMatching(locationState.leg.route);
                    initializeAllMarkers(locationState.leg.route);
                }
            }  else {
                setExpanded(true);
                setIsRouteBuilderMode(true);
            }
            
            // Add click listener for route building mode
            if (!locationState.leg.isKml) {
                mapRef.current.on('click', (e) => {
                    if (isRouteBuilderMode) {
                        addWaypoint(e.lngLat.lng, e.lngLat.lat);
                    }
                });
            }
        });
        
        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
            }
        }
    }, [location, isRouteBuilderMode]);

    //Use this to clear the sessionStorage of the react router state property, so the user is forwarded to the home page
    //If you do not do this, the user might work for hours on the map tool only to realize after clicking on the save button
    //that nothing was saved.
    useEffect(() => {
        window.history.replaceState({}, document.title)
    }, []);

    function initializeAllMarkers(waypoints: Waypoint[]) {
        markersRef.current.forEach((marker) => marker.remove());
        markersRef.current.clear();

        for (let i = 0; i < waypoints.length; i++) {
            addMarkerAtWaypointLocation(waypoints[i], i, waypoints.length - 1);
        }
    }
    
    function updateRouteOnMap(waypoints: Waypoint[]) {
        console.log("updateRouteOnMap:" + waypoints.length);
        if (isKml) { return; }
        
        // Handle red route line
        if (waypoints.length <= 1) {
            clearMatchedRouteOnMap();
        } else if (waypoints.length > 1) {
            // request api and show result route line on the map
            // also add start and endMarker
        }
    }
    function addSvgMarker(longitude: number, latitude: number, svgUrl: string) {
        const el = document.createElement('div');
        el.className = 'custom-svg-marker';
        el.style.width = '40px';
        el.style.height = '48px';
        el.style.backgroundImage = svgUrl;
        el.style.backgroundSize = '100%';
        el.style.backgroundRepeat = 'no-repeat';

        const markerOffset = [20, -20];
        return new mapboxgl.Marker({element: el, draggable: isRouteBuilderMode})
            .setLngLat([longitude, latitude])
            .setOffset(markerOffset)
            .addTo(mapRef.current);
    }
    
    // function addMarkerToMap(waypoint: Waypoint[], index: int, lastIndex: int) {
    //     const el = document.createElement('div');
    //     const markerOffset = [20, -20];
    //     if (index === 0) {
    //         el.className = 'custom-svg-marker';
    //         el.style.width = '40px';
    //         el.style.height = '48px';
    //         el.style.backgroundImage = `url(${startSvg})`;
    //         el.style.backgroundSize = '100%';
    //         el.style.backgroundRepeat = 'no-repeat';
    //     } else if (index === lastIndex) {
    //         el.className = 'custom-svg-marker';
    //         el.style.width = '40px';
    //         el.style.height = '48px';
    //         el.style.backgroundImage = `url(${endSvg})`;
    //         el.style.backgroundSize = '100%';
    //         el.style.backgroundRepeat = 'no-repeat';
    //     } else {
    //         el.className = 'waypoint-label';
    //         el.textContent = String.fromCharCode(65 + idx - 1);
    //     }
    //     const marker = new mapboxgl.Marker({element: el, draggable: isRouteBuilderMode })
    //         .setLngLat([waypoint.coordinates.lng, waypoint.coordinates.lat])
    //         .setOffset(markerOffset)
    //         .addTo(mapRef.current);
    //
    //     // marker.on('dragstart', () => {
    //     //     setDraggingMarkerRef(marker);
    //     // });
    //     //
    //     // marker.on('dragend', () => {
    //     //     const newLngLat = marker.getLngLat();
    //     //     updateWaypointPosition(waypointIndex, newLngLat.lng, newLngLat.lat);
    //     //     setDraggingMarkerRef(null);
    //     // });
    //
    //     // Store the marker reference for later cleanup
    //     markersRef.current.push({ marker, index });
    // }

    function addMarkerAtWaypointLocation(waypoint: Waypoint, index: int, lastIndex: int) {
        let marker = null;
        if (index === 0) {
            marker = addSvgMarker(waypoint.coordinates.lng, waypoint.coordinates.lat, `url(${startSvg})`);
        }  else if (index === lastIndex && !isRouteBuilderMode) {
            marker = addSvgMarker(waypoint.coordinates.lng, waypoint.coordinates.lat, `url(${endSvg})`);
        }
        else {
            // marker = new mapboxgl.Marker({ draggable: isRouteBuilderMode })
            //     .setLngLat([waypoint.coordinates.lng, waypoint.coordinates.lat])
            //     .addTo(mapRef.current);
            
            // const el = document.createElement('div');
            // el.className = 'marker-pin';
            // el.textContent = String(index + 1);


            const el = document.createElement('div');
            el.className = 'marker-pin';
            const label = document.createElement('span');
            label.className = 'marker-label';
            label.textContent = (index + 1) < 10 ? `0${index + 1}` : String(index + 1);
            el.appendChild(label);
            
            marker = new mapboxgl.Marker({
                element: el,
                draggable: isRouteBuilderMode,
                rotation: -45,
                offset:[14, 8]
            })
                .setLngLat([waypoint.coordinates.lng, waypoint.coordinates.lat])
                .addTo(mapRef.current!);
        }
        
        // Add drag events
        marker.on('dragstart', () => {
            setDraggingMarkerRef(marker);
        });

        marker.on('dragend', () => {
            const newLngLat = marker.getLngLat();
            updateWaypointPosition(waypoint.id, newLngLat.lng, newLngLat.lat);
            setDraggingMarkerRef(null);
        });
        
        markersRef.current.set(waypoint.id, marker);
    }
    
    function addWaypoint(lng, lat) {
        setWaypoints(prev => {
            if (prev.length == 100) {
                return prev;
            }
            
            const newWaypoint = {
                type: WaypointType.Waypoint,
                coordinates: { lng, lat },
                id: Guid.newGuid(),
            };
            addMarkerAtWaypointLocation(newWaypoint, prev.length);
            const newWaypoints = [...prev, newWaypoint];
            if (prev.length + 1 >= 2) {
                updateMapMatching(newWaypoints);
            }
            return newWaypoints;
        });
    }
    
    // Function to update a waypoint's position when it's dragged
    function updateWaypointPosition(id, lng, lat) {
        setWaypoints(prev => {
            const idx = prev.findIndex(wp => wp.id === id);
            if (idx === -1) return prev;

            const next = [...prev];
            next[idx] = { ...next[idx], coordinates: { lng, lat } };
            if (next.length >= 2) {
                updateMapMatching(next);
            }
            return next;
        });
    }
    
    async function updateMapMatching(waypointsToMatch: Waypoint[]) {
        if (waypointsToMatch.length < 2) return;
        
        setLoading(true);
        const coordinates = waypointsToMatch.map(wp => [wp.coordinates.lng, wp.coordinates.lat]);
        try {
            const responseData = await mapMatchingRequest(coordinates);
            displayMatchedRouteOnMap({ 
                type: "Feature",
                geometry: responseData,
                properties: {}
            }, waypointsToMatch);
        } catch (error) {
            console.error("Error updating map matching:", error);
        } finally {
            setLoading(false);
        }
    }
    
    function clearMatchedRouteOnMap() {
        if (mapRef.current.getSource('matched-route')) {
            mapRef.current.removeLayer('matched-route');
            mapRef.current.removeSource('matched-route');
        }

        if (mapRef.current.getSource('waypoints')) {
            mapRef.current.removeLayer('waypoint-points');
            mapRef.current.removeSource('waypoints');
        }
    }
    
    // Function to clear all waypoints
    function clearWaypoints() {
        // Remove all markers
        markersRef.current.forEach(({ marker }) => marker.remove());
        markersRef.current = [];
        
        // Clear waypoints state
        setWaypoints([]);
        
        // Remove the matched route if it exists
        if (mapRef.current.getSource('matched-route')) {
            mapRef.current.removeLayer('matched-route');
            mapRef.current.removeSource('matched-route');
        }
        
        if (mapRef.current.getSource('waypoints')) {
            mapRef.current.removeLayer('waypoint-points');
            mapRef.current.removeSource('waypoints');
        }
    }
    
    function toggleRouteBuilderMode() {
        setIsRouteBuilderMode(!isRouteBuilderMode);
    }

    function calculateBounds(waypoints: Waypoint[]): Bounds {
        if (waypoints.length === 0) {
            throw new Error("Waypoint list is empty.");
        }

        let minLat = Infinity;
        let minLng = Infinity;
        let maxLat = -Infinity;
        let maxLng = -Infinity;

        waypoints.forEach(({ coordinates }) => {
            minLat = Math.min(minLat, coordinates.lat);
            minLng = Math.min(minLng, coordinates.lng);
            maxLat = Math.max(maxLat, coordinates.lat);
            maxLng = Math.max(maxLng, coordinates.lng);
        });

        const southwest: google.maps.LatLngLiteral = { lat: minLat, lng: minLng };
        const northeast: google.maps.LatLngLiteral = { lat: maxLat, lng: maxLng };

        return { southwest, northeast };
    }

    function waypointsToGeoJsonLineString(waypoints: google.maps.LatLngLiteral[]): any {
        const coordinates = waypoints.map((waypoint) => [waypoint.lng, waypoint.lat]);

        const geoJsonLineString = {
            type: "Feature",
            geometry: {
                type: "LineString",
                coordinates: coordinates,
            },
            properties: {
            }
        };

        return geoJsonLineString;
    }

    async function mapMatchingRequest(coordinates: Array<[number, number]>): Promise<any> {
        const MAX_COORDINATES = 100;
        const chunks: Array<Array<[number, number]>> = [];

        for (let i = 0; i < coordinates.length; i += MAX_COORDINATES) {
            chunks.push(coordinates.slice(i, i + MAX_COORDINATES));
        }

        const createUrl = (coords: Array<[number, number]>) => {
            const coordinatesString = coords.map(coord => coord.join(',')).join(';');
            const coordinateAccuracyString = Array(coords.length).fill(25).join(';');

            return `https://api.mapbox.com/matching/v5/mapbox/driving/${coordinatesString}` +
                `?access_token=${accessToken}` +
                `&radiuses=${coordinateAccuracyString}` +
                `&geometries=geojson` +
                `&tidy=true` +
                // `&ignore=access,oneways,restrictions` +
                `&overview=full`;
        };

        const responses = await Promise.all(
            chunks.map(async (chunk) => {
                const url = createUrl(chunk);
                try {
                    const response = await fetch(url, { method: 'GET' });
                    if (!response.ok) {
                        throw new Error(`Error ${response.status}: ${response.statusText}`);
                    }
                    return response.json();
                } catch (error) {
                    console.error('Error in Map Matching API request:', error);
                    return null; // Handle or skip failed requests as needed
                }
            })
        );

        const mergedGeometry = {
            type: "LineString",
            coordinates: responses
                .filter(response => response !== null) // Ensure only valid responses are merged
                .flatMap(response => getMatchingWithMaxDistance(response.matchings).geometry.coordinates)
        };

        return mergedGeometry;
    }

    function displayMatchedRouteOnMap(matchedData: any, waypoints: Waypoint[]) {
        if (mapRef.current.getSource('matched-route')) {
            mapRef.current.removeLayer('matched-route');
            mapRef.current.removeSource('matched-route');
        }
        
        if (mapRef.current.getSource('waypoints')) {
            mapRef.current.removeLayer('waypoint-points');
            mapRef.current.removeSource('waypoints');
        }

        if (waypoints.length < 2) { return; }
        const pointFeatures = waypoints.map((wp, i) => ({
            type: 'Feature',
            properties: { waypointIndex: i },
            geometry: {
                type: 'Point' as const,
                coordinates: [ wp.coordinates.lng, wp.coordinates.lat ]
            }
        }));

        mapRef.current.addSource('waypoints', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: pointFeatures
            }
        });

        // Add the matched route as a new source
        mapRef.current.addSource('matched-route', {
            type: 'geojson',
            data: matchedData
        });

        // Add a new layer to visualize the route
        mapRef.current.addLayer({
            id: 'matched-route',
            type: 'line',
            source: 'matched-route',
            layout: {
                'line-join': 'round',
                'line-cap': 'round'
            },
            paint: {
                'line-color': '#F44336', // Red line color
                'line-width': 3 // Line width
            }
        });

        mapRef.current.addLayer({
            id: 'waypoint-points',
            type: 'circle',
            source: 'waypoints',
            paint: {
                'circle-radius': 6,
                'circle-color': '#F44336'
            }
        });
    }

    const handleToggle = () => {
        setExpanded((prev) => !prev);
    };

    function handleResponseAndSaveWaypoints(responseData, optimizedWaypoints: Waypoint[], realTolerance: number) {
        displayMatchedRouteOnMap(responseData, optimizedWaypoints);
        // Step 4: Save the optimized waypoints to the waypoints state so they get saved when pressing the save button
        setWaypoints(optimizedWaypoints);
        setAppliedTolerance(realTolerance);
        setLoading(false);
    }

    const handleApply = async (realTolerance: number) => {
        const startTime = Date.now();
        setLoading(true);
        const locationState = location.state as EditorState;
        const kmlCoordinates = locationState.leg.kmlCoordinates;
        // Step 1: Get optimized waypoints from the current tolerance settings and the kml coordinates
        const optimizedWaypoints = optimizeRoute(kmlCoordinates.map(c => ({
            type: WaypointType.Waypoint,
            coordinates: {lat: c.lat, lng: c.lng}
        })), realTolerance);

        // Step 2: Call the mapbox matching api with the newly optimized waypoints
        const responseData = await mapMatchingRequest(optimizedWaypoints.map(waypoint => [waypoint.coordinates.lng, waypoint.coordinates.lat]));
        // Step 3: Parse the result of the matching api and render it on the map
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime < 300) {
            const remainingTime = 300 - elapsedTime;
            setTimeout(() => {
                handleResponseAndSaveWaypoints(responseData, optimizedWaypoints, realTolerance);
            }, remainingTime);
        } else {
            handleResponseAndSaveWaypoints(responseData, optimizedWaypoints, realTolerance);
        }
    };
    
    function getHighestConfidenceEntry(matchings: Matching[]): Matching | undefined {
        if (matchings.length === 0) return undefined;

        return matchings.reduce((max, current) => {
            return current.confidence > max.confidence ? current : max;
        });
    }

    function getMatchingWithMaxDistance(matchings: Matching[]): Matching | undefined {
        if (matchings.length === 0) {
            return undefined; // Return undefined if the array is empty
        }

        // Initialize the max matching as the first matching with a non-zero distance, if found
        let maxDistanceMatching: Matching | undefined = matchings[0].distance > 0 ? matchings[0] : undefined;

        for (const matching of matchings) {
            if (matching.distance > 0 && (!maxDistanceMatching || matching.distance > maxDistanceMatching.distance)) {
                maxDistanceMatching = matching;
            }
        }

        return maxDistanceMatching;
    }
    
    function deleteWaypoint(id: string) {
        let newWaypoints = waypoints.filter((wp, _) => wp.id !== id);
        setWaypoints(newWaypoints);
        updateMapMatching(newWaypoints);
        initializeAllMarkers(newWaypoints);
    }

    function removeWaypointMarkerFromMap(id: string) {
        const marker = markersRef.current.get(id);
        if (!marker) return;
        marker.remove();
        markersRef.current.delete(id);
    }

    const handleSave = async () => {
        setLoading(true);
        const locationState = location.state as EditorState;
        const updatedLeg = structuredClone(locationState.leg);
        for (let i = 0; i < waypoints.length; i++) {
            let wp = waypoints[i];
            if (i === 0) {
                wp.type = WaypointType.Start;
            } else if (i === waypoints.length - 1) {
                wp.type = WaypointType.End;
            } else {
                wp.type = WaypointType.Waypoint;
            }
        }
        
        updatedLeg.route = waypoints;
        updatedLeg.tolerance = tolerance;
        await updateLeg(locationState.dayId, updatedLeg);
        setLoading(false);
        navigate("/");
    }

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return
        }

        const items = reorder(
            waypoints,
            result.source.index,
            result.destination.index
        )

        setWaypoints(items);
        initializeAllMarkers(items);
        updateMapMatching(items);
    }

    const reorder = (waypoints: Waypoint[], startIndex: number, endIndex: number): Waypoint[] => {
        const result = Array.from(waypoints);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    }
    
    return (
        <>
            {/* Full-page loading overlay */}
            {loading && (
                <Box
                    position="fixed"
                    top={0}
                    left={0}
                    width="100vw"
                    height="100vh"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(255, 255, 255, 0.7)"
                    zIndex={9999}
                >
                    <CircularProgress />
                </Box>
            )}

            {/* Main content */}
            <div id='map-container' style={{ width: '100vw', height: '100vh', }} ref={mapContainerRef}/>
            <div style={{position: 'absolute', top: '16px', left: '16px'}}>
                <div style={{display: 'flex'}}>
                    <IconButton aria-label="dragdrop" sx={{
                        backgroundColor: 'white',
                        boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)'
                    }} disableRipple onClick={() => {navigate("/")}}><ArrowBackIcon /></IconButton>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        padding: '6px 16px 4px 16px',
                        marginLeft: '8px',
                        minWidth: '400px',
                        boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)'
                    }}>
                        <Typography sx={{ textAlign: 'left' }} variant="subtitle1">{legTitle}</Typography>
                    </div>
                </div>

                {isKml && (
                    <Paper elevation={2} style={{ padding: '0px 12px 0px 16px', marginTop: '8px', width: '452px' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography sx={{paddingTop: '6px'}} variant="subtitle1" gutterBottom>Legend</Typography>
                            <Box display="flex" alignItems="center" mr={2}>
                                <Box sx={{ width: '20px', height: '20px', backgroundColor: '#1976d2', marginRight: '8px' }} />
                                <Typography variant="body2">Original Route</Typography>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <Box sx={{ width: '20px', height: '20px', backgroundColor: '#F44336', marginRight: '8px' }} />
                                <Typography variant="body2">App Preview</Typography>
                            </Box>
                        </Box>
                    </Paper>
                )}

                {!isKml && (
                    <Paper elevation={2} style={{padding: '6px 8px 4px 16px', marginTop: '8px', width: '456px'}}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" onClick={handleToggle}
                             style={{cursor: 'pointer'}}>
                            <Typography variant="subtitle1">Route Builder</Typography>
                            <IconButton size="small">
                                {expanded ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                            </IconButton>
                        </Box>

                        <Collapse in={expanded}>
                            <Box mt={2} sx={{marginRight: '8px', marginBottom: '12px'}}>
                                <Typography variant="body2" gutterBottom>
                                    {isRouteBuilderMode
                                        ? "Click on the map to add waypoints."
                                        : "Enable route builder mode to create a custom route by clicking on the map."}
                                </Typography>

                                <Box display="flex" gap={2} justifyContent="normal" marginTop="16px">
                                    {isRouteBuilderMode && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            fullWidth
                                            onClick={toggleRouteBuilderMode}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    
                                    <Button
                                        variant={isRouteBuilderMode ? "contained" : "outlined"}
                                        fullWidth
                                        onClick={isRouteBuilderMode ? handleSave : toggleRouteBuilderMode}
                                    >
                                        {isRouteBuilderMode ? "Save" : "Start Route Building"}
                                    </Button>

                                </Box>
                                <Sidebar waypoints={waypoints} onDeleteClick={deleteWaypoint}
                                         onTypeChange={handleSave} onDragEnd={onDragEnd} editEnabled={isRouteBuilderMode} />
                            </Box>
                        </Collapse>
                    </Paper>
                )}
            </div>
        </>
    );
}

export default MapBoxEditor;
